import React from "react";
import classnames from "classnames";
import isFunction from "lodash/isFunction";

import { formats, licenses, licensesDisplay } from "../../reducers/download";

import DownloadFlowErrors from "./download-flow-errors";
import LicenseStrip from "./license-strip";
import IconSaveForLater from "./icon-save-for-later";
import IconCollection from "./icon-collection";
import IconTags from "./icon-tags";
import ToolTip from "../tool-tip";
import { ExportSizesControl } from "../editor/editor-controls";

const CreativeCommonsDownloadReady = props => {
    let { pendingDownload } = props.download;
    let attributionText;

    if (pendingDownload.uploader) {
        attributionText =
            pendingDownload.term +
            " by " +
            pendingDownload.uploader.name +
            " from the Noun Project";
    }

    return (
        <div className="license-specific-content">
            <div className="attribution-instructions">
                <h3>You must add attribution:</h3>
                <ul>
                    <li>
                        Use the embedded attribution if the icon is larger than
                        100px.
                    </li>
                    <li>
                        Copy the attribution text below and include it in a
                        bibliography or image caption, or on a physical item or
                        its tag.
                    </li>
                </ul>
                <a
                    href="https://thenounproject.zendesk.com/hc/en-us/articles/200509928-How-do-I-give-creators-credit-in-my-work-"
                    target="_blank"
                >
                    Learn More
                </a>
                <h3>Attribution text</h3>
                <div className="copy-attribution">
                    <textarea
                        id="copy-attribution"
                        className="copy-input"
                        value={attributionText}
                        readOnly
                        onClick={selectText}
                    />
                </div>
                <p>
                    <a
                        href="https://thenounproject.zendesk.com/hc/en-us/articles/203605383-Can-I-trademark-an-icon-to-use-for-my-business-logo-"
                        target="_blank"
                    >
                        Logo or brand use? Get special info
                    </a>
                </p>
            </div>
        </div>
    );
};

const selectText = event => event.target.select();

const PhaseDownloadReady = props => {
    let {
        pendingDownload,
        license,
        processing,
        format,
        justPurchased,
        prepaidBalance
    } = props.download;

    // Show supplementary info on direct to Download Ready flows
    let showInfo =
        license != licenses.ROYALTY_FREE &&
        license != licenses.CREATIVE_COMMONS;

    let tags = <IconTags {...props} />;

    let licenseSpecificContent;
    if (license == licenses.CREATIVE_COMMONS) {
        licenseSpecificContent = CreativeCommonsDownloadReady(props);
        tags = null;
    } else if (justPurchased) {
        let licenseDisplay = licensesDisplay[license];

        if (licenseDisplay && licenseDisplay.thankYouText) {
            let { thankYouText } = licenseDisplay;
            if (isFunction(thankYouText)) {
                thankYouText = thankYouText();
            }

            licenseSpecificContent = (
                <div className="license-thank-you license-specific-content">
                    <h1>{licenseDisplay.thankYouTitle}</h1>
                    <p>{thankYouText}</p>
                </div>
            );
        }
    }

    let repurchase;
    if (license == licenses.ALREADY_PURCHASED_ROYALTY_FREE) {
        let repurchaseHelp = (
            <ToolTip>
                <p>Repurchase if you need an extended use license.</p>
                <a
                    className="text-link"
                    href="https://thenounproject.zendesk.com/hc/en-us/articles/200562967-Can-I-use-icons-in-Items-for-Resale-"
                    target="_blank"
                >
                    Learn more
                </a>
            </ToolTip>
        );

        repurchase = (
            <div className="repurchase-license">
                <span
                    className="link-action"
                    onClick={props.downloadRepurchase.bind(
                        null,
                        pendingDownload
                    )}
                >
                    Purchase another license
                </span>{" "}
                {repurchaseHelp}
            </div>
        );
    }

    let canExportSize = license != licenses.CREATIVE_COMMONS;
    let exportSize =
        canExportSize &&
        props.editor &&
        props.editor.transforms &&
        props.editor.transforms.exportSize;

    let formatList = formats.map(formatOption => {
        let options;
        let formatDisplay = formatOption;

        let onButtonClick = props.downloadStart.bind(null, formatOption);

        if (canExportSize) {
            if (exportSize && formatOption == "png") {
                let control = (
                    <span>
                        <i className="ui_down-drop-arrow" />
                    </span>
                );
                options = (
                    <ExportSizesControl
                        control={control}
                        exportSize={exportSize}
                        editorTransform={props.editorTransform}
                    />
                );

                formatDisplay = (
                    <span className="button-target" onClick={onButtonClick}>
                        {formatDisplay}
                    </span>
                );

                onButtonClick = null;
            }
        }

        let formatClass = classnames("option format-option button", {
            current: format == formatOption,
            "with-options": !!options
        });

        return (
            <li
                className={formatClass}
                key={formatOption}
                onClick={onButtonClick}
            >
                {formatDisplay}
                {options}
            </li>
        );
    });

    let formatHelp = (
        <ToolTip className="format-tool-tip">
            <p>
                Icons can be downloaded as two different file types, PNG or SVG.
            </p>
            <p>
                <b>PNG</b> are raster images that can be used anywhere you can
                use a photo. They are best for use in mediums like presentations
                or wireframes. Pixelation may occur when scaling up.
            </p>

            <p>
                <b>SVG</b> are vector images that are infinitely scalable, so
                they never pixelate. They are best for use in mediums like print
                and websites.
            </p>
            <a
                href="https://thenounproject.zendesk.com/hc/en-us/articles/204515707-What-s-the-difference-between-a-PNG-and-SVG-"
                target="_blank"
                className="text-link"
            >
                Read More
            </a>
        </ToolTip>
    );

    let saveForLater, collection;
    if (showInfo) {
        collection = <IconCollection {...props} />;
        saveForLater = <IconSaveForLater {...props} />;
    }

    let classes = classnames("download-ready-container", {
        processing
    });

    let licenseStrip = license != licenses.CREATIVE_COMMONS && (
        <LicenseStrip license={license} />
    );

    let exportInfo;
    if (canExportSize) {
        exportInfo = (
            <div className="export-info">
                {exportSize}
                px x {exportSize}
                px
            </div>
        );
    }

    return (
        <div className={classes}>
            {collection}
            {licenseSpecificContent}
            {licenseStrip}
            {repurchase}
            <div className="download-actions">
                <h4>Download Icon {formatHelp}</h4>
                <div className="relative-wrapper">
                    <ul className="format-options">{formatList}</ul>
                    {exportInfo}
                    <DownloadFlowErrors error={props.download.error} />
                    {saveForLater}
                </div>
            </div>
            {tags}
        </div>
    );
};

PhaseDownloadReady.handlesOwnErrors = true;

export default PhaseDownloadReady;
